import type { JointMotion } from "@/shared/components/visualization/Panel/ThreeDPanel/BabylonManager";
import { isIndependentlyDrivable } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import { Slider } from "@/shared/foundation/primitives";
import type {
  Joint,
  JointBinding,
} from "@/shared/state/visualization/schema/v1/three-d";

import { JointBindingRow } from "./JointBindingRow";
import styles from "./JointControls.module.css";
import { jointControl } from "./jointPosing";

/**
 * How much of each joint's row is on show.
 *
 * The two are **additive, not exclusive**: `bind` is `pose` plus the binding
 * editor. Deciding what to bind a joint to and confirming that the joint moves
 * the way you expect are the same sitting — you drag the slider, watch the arm,
 * and then say where that value comes from — so making them alternatives would
 * mean toggling back and forth to do one job.
 *
 * `pose` therefore drops only the editor: the sliders alone, for reading a
 * twenty-joint robot as a list of axes rather than a wall of inputs.
 */
export type JointSectionMode = "pose" | "bind";

interface JointControlsProps {
  joints: Joint[];
  /** The value a joint currently sits at: radians if it turns, metres if it slides. */
  valueOf: (jointName: string) => number;
  /** Poses one joint. */
  onChange: (jointName: string, value: number) => void;
  /**
   * What each joint is declared to be driven by, keyed by joint name.
   *
   * Every joint that can carry a declaration gets its own picker, so a robot
   * whose arm and gripper are published by different nodes is expressible. The
   * bulk control above the list is a shortcut for the common single-`JointState`
   * case, not the only way in.
   */
  bindings?: Record<string, JointBinding>;
  /**
   * Edits one joint's declaration, or removes it with `undefined`. Omit to
   * render the list read-only — which is what happens when there is no source
   * URDF to save a declaration onto.
   */
  onBindingChange?: (
    jointName: string,
    binding: JointBinding | undefined,
  ) => void;
  /** Whose topic catalogue the pickers search. */
  orgId?: string;
  /**
   * How much of each row to show. Defaults to `pose`, which is all there is
   * when {@link onBindingChange} is absent and no mode control is offered.
   */
  mode?: JointSectionMode;
}

/**
 * Enough resolution to see a joint move without a slider that feels sticky.
 *
 * A hundredth of a radian is about half a degree. A hundredth of a *metre* is a
 * centimetre, which is a third of a gripper finger's entire travel — so the two
 * motions cannot share a step.
 */
const SLIDER_STEP = { rotate: 0.01, slide: 0.001 } as const;

/** Radians to two places, metres to three — see {@link SLIDER_STEP}. */
function formatJointValue(value: number, motion: JointMotion): string {
  return motion === "slide"
    ? `${value.toFixed(3)} m`
    : `${value.toFixed(2)} rad`;
}

/**
 * One row per joint of the model, with a slider for each that can be posed.
 *
 * Moving a slider hands one number to `onChange` and nothing else. This
 * component holds no model state and knows nothing about entities: where a
 * joint's value lives, and what turning it means, is `buildPreviewScene`'s
 * business.
 *
 * Joints that cannot be posed still get a row, stating why — see
 * {@link jointControl}. A robot whose gripper is simply missing from this list
 * would read as a broken conversion.
 *
 * ## Why the editor is hidden rather than unmounted
 *
 * The sliders are in both modes, so nothing can lose a pose. The binding editor
 * is not, and it is rendered on both anyway, with `hidden` taking it out of the
 * page and out of the accessibility tree on `pose`. Unmounting would read more
 * tidily and would lose data: {@link JointBindingRow} holds a *local* text
 * draft and a unit choice, and only publishes a declaration once the path it
 * has parses to a complete, valid one. A half-typed path, a path the catalogue
 * says still needs an element index, and a unit picked before the path was
 * finished are all state that exists nowhere else — so a trip out to the
 * sliders and back would silently empty rows the user was part-way through.
 *
 * Both halves of a joint stay inside that joint's own `<li>`, rather than being
 * split into a list of sliders and a list of pickers. Two parallel lists would
 * put the user in the position of aligning them by eye, and a pair that fell
 * out of step by one row would have them binding the wrong joint.
 */
export function JointControls({
  joints,
  valueOf,
  onChange,
  bindings,
  onBindingChange,
  orgId,
  mode = "pose",
}: JointControlsProps) {
  if (joints.length === 0) {
    return (
      <p className={styles.emptyNote}>
        This model declares no joints, so there is nothing to pose. It will
        still render, as a single rigid assembly.
      </p>
    );
  }

  return (
    <>
      <ul className={styles.jointList}>
        {joints.map((joint) => {
          const control = jointControl(joint);
          const value = valueOf(joint.name);
          const binding = bindings?.[joint.name];
          return (
            <li className={styles.jointRow} key={joint.name}>
              <div className={styles.jointHeader}>
                <span className={styles.jointName}>{joint.name}</span>
                <span className={styles.jointType}>{joint.type}</span>
              </div>
              {/* In both modes: posing a joint is how a user decides whether
                  the axis they are about to bind is the one they meant. */}
              {control.kind === "slider" ? (
                <div className={styles.sliderRow}>
                  <Slider
                    aria-label={joint.name}
                    max={control.max}
                    min={control.min}
                    onValueChange={(next) => onChange(joint.name, next)}
                    step={SLIDER_STEP[control.motion]}
                    value={value}
                    getAriaValueText={(next) =>
                      formatJointValue(next, control.motion)
                    }
                  />
                  <span className={styles.angleValue}>
                    {formatJointValue(value, control.motion)}
                  </span>
                </div>
              ) : (
                <span className={styles.disabledNote}>{control.reason}</span>
              )}
              {/* A joint the data cannot drive gets no picker at all: a coupled
                  joint's value is derived from the joint it follows, and a
                  fixed, floating or planar one has no single value to read.
                  `jointControl`'s row above already says which it is. */}
              {onBindingChange !== undefined &&
                isIndependentlyDrivable(joint) && (
                  <div hidden={mode !== "bind"}>
                    <JointBindingRow
                      binding={binding}
                      joint={joint}
                      onChange={(next) => onBindingChange(joint.name, next)}
                      orgId={orgId}
                    />
                  </div>
                )}
            </li>
          );
        })}
      </ul>
      <p className={styles.limitationNote}>
        Coupled (<code>mimic</code>) joints are not applied here — a joint that
        follows another in the robot description stays still in this preview.
        The relationship is preserved in the generated asset.
      </p>
    </>
  );
}
